"use client";

import Link from "next/link";
import { useState, use } from "react";
import { motion, AnimatePresence } from "motion/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import Footer from "@/components/ui/Footer/Footer";
import Icon from "@/components/ui/Icon/Icon";
import Logo from "@/components/ui/Logo/Logo";
import { useTranslations } from "next-intl";
import { useCommerceSettings } from "@/contexts/CommerceSettingsContext";

export default function TeamsLandingPage({ params }) {
    const { locale } = use(params);
    const t = useTranslations("teams.landing");
    // Essai Teams : interrupteur et durée pilotés depuis /admin/promotions,
    // les mêmes que ceux appliqués par le serveur au moment du paiement.
    const { settings: commerceSettings } = useCommerceSettings();
    const teamsTrialEnabled = commerceSettings.trial.teams.enabled;
    const teamsTrialDays = commerceSettings.trial.teams.days;
    const [openFaq, setOpenFaq] = useState(null);
    const [activeIndustry, setActiveIndustry] = useState("tech");

    const industries = [
        { id: "tech", label: t("industriesTech") },
        { id: "finance", label: t("industriesFinance") },
        { id: "consulting", label: t("industriesConsulting") },
        { id: "agencies", label: t("industriesAgencies") },
    ];

    const testimonialsByIndustry = {
        tech: {
            quote: t("testimonialTechQuote"),
            highlight: t("testimonialTechHighlight"),
            company: "Doctolib",
            author: "Marie Laurent",
            role: t("testimonialTechRole"),
            avatar: "ML",
        },
        finance: {
            quote: t("testimonialFinanceQuote"),
            highlight: t("testimonialFinanceHighlight"),
            company: "Deloitte",
            author: "Julie Martin",
            role: t("testimonialFinanceRole"),
            avatar: "JM",
        },
        consulting: {
            quote: t("testimonialConsultingQuote"),
            highlight: t("testimonialConsultingHighlight"),
            company: "Qonto",
            author: "Marc Lefèvre",
            role: t("testimonialConsultingRole"),
            avatar: "ML",
        },
        agencies: {
            quote: t("testimonialAgenciesQuote"),
            highlight: t("testimonialAgenciesHighlight"),
            company: "PayFit",
            author: "Thomas Renard",
            role: t("testimonialAgenciesRole"),
            avatar: "TR",
        },
    };

    const trustedLogos = [
        { src: "/static/companies/google.webp", alt: "Google" },
        { src: "/static/companies/capgemini.webp", alt: "Capgemini" },
        { src: "/static/companies/deloitte.webp", alt: "Deloitte" },
        { src: "/static/companies/amazon.webp", alt: "Amazon" },
        { src: "/static/companies/ibm.webp", alt: "IBM" },
        { src: "/static/companies/accenture.webp", alt: "Accenture" },
    ];

    const mainFeatures = [
        {
            icon: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="1em"
                    height="1em"
                    viewBox="0 0 24 24"
                    className="w-full h-full"
                >
                    <g fill="none" stroke="currentColor" strokeWidth="1.5">
                        <circle cx="12" cy="13" r="9" />
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 9v4l2.5 2.5m-11-11l4-2.5m13 2.5l-4-2.5"
                        />
                    </g>
                </svg>
            ),
            title: t("feature1Title"),
            description: t("feature1Desc"),
        },
        {
            icon: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="1em"
                    height="1em"
                    viewBox="0 0 24 24"
                    className="w-full h-full"
                >
                    <g fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12S6.477 2 12 2s10 4.477 10 10Z" />
                        <path
                            strokeLinecap="round"
                            d="M2 12h3m14 0h3M12 22v-3m0-14V2"
                        />
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M10 12h4m-2 2v-4"
                        />
                    </g>
                </svg>
            ),
            title: t("feature2Title"),
            description: t("feature2Desc"),
        },
        {
            icon: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="1em"
                    height="1em"
                    viewBox="0 0 24 24"
                    className="w-full h-full"
                >
                    <g fill="none" stroke="currentColor" strokeWidth="1.5">
                        <circle cx="9" cy="6" r="4" />
                        <path strokeLinecap="round" d="M15 9a3 3 0 1 0 0-6" />
                        <ellipse cx="9" cy="17" rx="7" ry="4" />
                        <path
                            strokeLinecap="round"
                            d="M18 14c1.754.385 3 1.359 3 2.5c0 1.03-1.014 1.923-2.5 2.37"
                        />
                    </g>
                </svg>
            ),
            title: t("feature3Title"),
            description: t("feature3Desc"),
        },
        {
            icon: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="1em"
                    height="1em"
                    viewBox="0 0 24 24"
                    className="w-full h-full"
                >
                    <g fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M2 12c0-4.714 0-7.071 1.464-8.536C4.93 2 7.286 2 12 2s7.071 0 8.535 1.464C22 4.93 22 7.286 22 12s0 7.071-1.465 8.535C19.072 22 16.714 22 12 22s-7.071 0-8.536-1.465C2 19.072 2 16.714 2 12Z" />
                        <path
                            strokeLinecap="round"
                            d="m7 14l1.797-2.156c.712-.855 1.068-1.282 1.536-1.282c.469 0 .825.427 1.537 1.282l.26.312c.712.855 1.068 1.282 1.537 1.282s.824-.427 1.536-1.282L17 10"
                        />
                    </g>
                </svg>
            ),
            title: t("feature4Title"),
            description: t("feature4Desc"),
        },
    ];

    const expertisePoints = [
        {
            icon: <SolarIcon name="square-academic-cap-linear" className="w-5 h-5" />,
            title: t("expertisePoint1Title"),
            desc: t("expertisePoint1Desc"),
        },
        {
            icon: <SolarIcon name="bolt-linear" className="w-5 h-5" />,
            title: t("expertisePoint2Title"),
            desc: t("expertisePoint2Desc"),
        },
        {
            icon: <SolarIcon name="layers-minimalistic-linear" className="w-5 h-5" />,
            title: t("expertisePoint3Title"),
            desc: t("expertisePoint3Desc"),
        },
        {
            icon: <SolarIcon name="medal-ribbon-star-linear" className="w-5 h-5" />,
            title: t("expertisePoint4Title"),
            desc: t("expertisePoint4Desc"),
        },
    ];

    const upskillingPoints = [
        {
            icon: <SolarIcon name="bolt-linear" className="w-5 h-5" />,
            title: t("upskillingPoint1Title"),
            desc: t("upskillingPoint1Desc"),
        },
        {
            icon: <SolarIcon name="medal-ribbon-star-linear" className="w-5 h-5" />,
            title: t("upskillingPoint2Title"),
            desc: t("upskillingPoint2Desc"),
        },
        {
            icon: <SolarIcon name="cup-star-linear" className="w-5 h-5" />,
            title: t("upskillingPoint3Title"),
            desc: t("upskillingPoint3Desc"),
        },
        {
            icon: <SolarIcon name="gamepad-bold-duotone" className="w-5 h-5" />,
            title: t("upskillingPoint4Title"),
            desc: t("upskillingPoint4Desc"),
        },
    ];

    const trackingPoints = [
        {
            icon: <SolarIcon name="eye-linear" className="w-5 h-5" />,
            title: t("trackingPoint1Title"),
            desc: t("trackingPoint1Desc"),
        },
        {
            icon: <SolarIcon name="chart-2-linear" className="w-5 h-5" />,
            title: t("trackingPoint2Title"),
            desc: t("trackingPoint2Desc"),
        },
        {
            icon: <SolarIcon name="file-text-linear" className="w-5 h-5" />,
            title: t("trackingPoint3Title"),
            desc: t("trackingPoint3Desc"),
        },
        {
            icon: <SolarIcon name="lightbulb-linear" className="w-5 h-5" />,
            title: t("trackingPoint4Title"),
            desc: t("trackingPoint4Desc"),
        },
    ];

    const certificationPoints = [
        {
            icon: <SolarIcon name="medal-ribbon-star-linear" className="w-5 h-5" />,
            title: t("certificationPoint1Title"),
            desc: t("certificationPoint1Desc"),
        },
        {
            icon: <SolarIcon name="brain-linear" className="w-5 h-5" />,
            title: t("certificationPoint2Title"),
            desc: t("certificationPoint2Desc"),
        },
        {
            icon: <SolarIcon name="check-circle-linear" className="w-5 h-5" />,
            title: t("certificationPoint3Title"),
            desc: t("certificationPoint3Desc"),
        },
        {
            icon: <SolarIcon name="graph-up-linear" className="w-5 h-5" />,
            title: t("certificationPoint4Title"),
            desc: t("certificationPoint4Desc"),
        },
    ];

    const skillMappingPoints = [
        {
            icon: <SolarIcon name="chart-2-linear" className="w-5 h-5" />,
            title: t("skillMappingPoint1Title"),
            desc: t("skillMappingPoint1Desc"),
        },
        {
            icon: <SolarIcon name="magnifer-linear" className="w-5 h-5" />,
            title: t("skillMappingPoint2Title"),
            desc: t("skillMappingPoint2Desc"),
        },
        {
            icon: <SolarIcon name="flag-linear" className="w-5 h-5" />,
            title: t("skillMappingPoint3Title"),
            desc: t("skillMappingPoint3Desc"),
        },
        {
            icon: <SolarIcon name="users-group-two-rounded-linear" className="w-5 h-5" />,
            title: t("skillMappingPoint4Title"),
            desc: t("skillMappingPoint4Desc"),
        },
    ];

    const customPathsPoints = [
        {
            icon: <SolarIcon name="routing-linear" className="w-5 h-5" />,
            title: t("customPathsPoint1Title"),
            desc: t("customPathsPoint1Desc"),
        },
        {
            icon: <SolarIcon name="settings-linear" className="w-5 h-5" />,
            title: t("customPathsPoint2Title"),
            desc: t("customPathsPoint2Desc"),
        },
        {
            icon: <SolarIcon name="bot-linear" className="w-5 h-5" />,
            title: t("customPathsPoint3Title"),
            desc: t("customPathsPoint3Desc"),
        },
        {
            icon: <SolarIcon name="target-linear" className="w-5 h-5" />,
            title: t("customPathsPoint4Title"),
            desc: t("customPathsPoint4Desc"),
        },
    ];

    const stats = [
        {
            value: `${process.env.NEXT_PUBLIC_NUMBER_OF_TEAMS}+`,
            label: t("stat1Label"),
        },
        {
            value: t("stat2Value"),
            label: t("stat2Label"),
        },
        {
            value: `${process.env.TRUSTPILOT_NOTE}/5`,
            label: t("stat3Label"),
        },
    ];

    const faqs = [
        {
            question: t("faq1Question"),
            answer: t("faq1Answer"),
        },
        {
            question: t("faq2Question"),
            answer: t("faq2Answer"),
        },
        {
            question: t("faq3Question"),
            answer: t("faq3Answer"),
        },
        {
            question: t("faq4Question"),
            answer: t("faq4Answer"),
        },
        {
            question: t("faq5Question"),
            answer: t("faq5Answer"),
        },
        {
            question: t("faq6Question"),
            answer: t("faq6Answer"),
        },
    ];

    const currentTestimonial = testimonialsByIndustry[activeIndustry];

    return (
        <div className="min-h-screen bg-white dark:bg-grayDark-950">
            {/* Promo Banner */}
            {teamsTrialEnabled && (
                <div className="bg-grayDark-900 text-white py-2.5 sm:py-3 px-4 border-b border-grayDark-800">
                    <div className="max-w-6xl mx-auto flex items-center justify-center gap-2 sm:gap-4 flex-wrap text-xs sm:text-sm">
                        <div className="flex items-center gap-2 sm:gap-3">
                            <span className="bg-primary px-2 sm:px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold">
                                {t("promoBadge")}
                            </span>
                            <span className="text-gray-300 hidden sm:inline">
                                {t("promoTextFull", { days: teamsTrialDays })}
                            </span>
                            <span className="text-gray-300 sm:hidden">
                                {t("promoTextShort", { days: teamsTrialDays })}
                            </span>
                        </div>
                        <Link
                            href={`/${locale}/teams/signup`}
                            className="text-primary font-medium hover:underline inline-flex items-center gap-1"
                        >
                            {t("promoLink")} <SolarIcon name="arrow-right-linear" className="w-3 h-3" />
                        </Link>
                    </div>
                </div>
            )}

            {/* Hero Section */}
            <section className="pt-20 lg:pt-28 pb-20 lg:pb-28 px-4">
                <div className="max-w-7xl mx-auto">
                    <div className="grid xl:grid-cols-2 gap-12 xl:gap-16 items-center">
                        {/* Left - Text */}
                        <div>
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-gray-900 dark:text-white mb-6 leading-[1.1] tracking-tight">
                                {t("heroTitle")}{" "}
                                <span className="text-primary">
                                    {t("heroTitleHighlight")}
                                </span>
                            </h1>

                            <p className="text-lg lg:text-xl text-gray-500 dark:text-grayDark-400 mb-10 leading-relaxed">
                                {t("heroSubtitle")}
                            </p>

                            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-12">
                                <Link
                                    href={`/${locale}/teams/signup`}
                                    className="inline-flex items-center justify-center gap-2 bg-primary btn-3d lip-orange text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-full font-medium hover:bg-primary/90 hover:text-white transition-all text-sm sm:text-base shadow-lg shadow-primary/25"
                                >
                                    {teamsTrialEnabled ? t("ctaTrial") : t("ctaNoTrial")}
                                </Link>
                                <Link
                                    href={`/${locale}/teams/demo`}
                                    className="inline-flex items-center justify-center gap-2 text-gray-600 dark:text-grayDark-300 px-6 sm:px-8 py-3.5 sm:py-4 rounded-full font-medium hover:text-gray-900 dark:hover:text-white transition-all text-sm sm:text-base"
                                >
                                    {t("ctaDemo")}{" "}
                                    <SolarIcon name="arrow-right-linear" className="w-4 h-4" />
                                </Link>
                            </div>

                            {/* Trusted logos */}
                            <div>
                                <p className="text-xs text-gray-400 dark:text-grayDark-500 mb-4 font-medium tracking-wide uppercase">
                                    {t("trustedBy")}
                                </p>
                                <div className="flex items-center gap-4 sm:gap-6 flex-wrap">
                                    {trustedLogos.slice(0, 4).map((logo, i) => (
                                        <img
                                            key={i}
                                            src={logo.src}
                                            alt={logo.alt}
                                            className="object-contain h-[14px] sm:h-[18px] brightness-0 dark:brightness-100 opacity-60"
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Right - Dashboard mockup */}
                        <div className="relative hidden sm:block">
                            <div className="bg-gray-950 dark:bg-grayDark-900 rounded-2xl p-1.5 shadow-2xl shadow-gray-900/20 dark:shadow-black/40 border border-grayDark-800">
                                <div className="bg-grayDark-900 rounded-xl overflow-hidden">
                                    {/* Browser bar */}
                                    <div className="bg-grayDark-800 px-3 py-2 flex items-center gap-2">
                                        <div className="flex gap-1.5">
                                            <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                                            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                                            <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                                        </div>
                                        <div className="flex-1 bg-grayDark-700 rounded h-5 max-w-[200px] mx-auto flex items-center justify-center">
                                            <span className="text-[10px] text-gray-400">
                                                app.believemy.com/teams
                                            </span>
                                        </div>
                                    </div>
                                    {/* Dashboard content */}
                                    <div className="p-3 bg-gray-50 dark:bg-grayDark-900">
                                        <div className="bg-white dark:bg-grayDark-800 rounded-lg border border-gray-200 dark:border-grayDark-700 p-4">
                                            {/* Stats row */}
                                            <div className="grid grid-cols-3 gap-3 mb-4">
                                                <div className="bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 rounded-lg p-3">
                                                    <div className="text-[10px] text-gray-500 dark:text-grayDark-400">
                                                        {t("mockupTeam")}
                                                    </div>
                                                    <div className="text-lg font-bold text-gray-900 dark:text-white">
                                                        12/15
                                                    </div>
                                                    <div className="text-[10px] text-green-500">
                                                        ↑ 8%
                                                    </div>
                                                </div>
                                                <div className="bg-gradient-to-br from-green-50 to-green-100/50 dark:from-green-900/30 dark:to-green-900/10 rounded-lg p-3">
                                                    <div className="text-[10px] text-gray-500 dark:text-grayDark-400">
                                                        {t("mockupSessions")}
                                                    </div>
                                                    <div className="text-lg font-bold text-gray-900 dark:text-white">
                                                        89
                                                    </div>
                                                    <div className="text-[10px] text-green-500">
                                                        ↑ 24%
                                                    </div>
                                                </div>
                                                <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 dark:from-purple-900/30 dark:to-purple-900/10 rounded-lg p-3">
                                                    <div className="text-[10px] text-gray-500 dark:text-grayDark-400">
                                                        {t("mockupTotalXp")}
                                                    </div>
                                                    <div className="text-lg font-bold text-gray-900 dark:text-white">
                                                        12.4K
                                                    </div>
                                                    <div className="text-[10px] text-purple-500">
                                                        {t("mockupLeague")}
                                                    </div>
                                                </div>
                                            </div>
                                            {/* Members list */}
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-grayDark-700/50 rounded-lg">
                                                    <img
                                                        src="/images/site/people/woman-1.jpg"
                                                        alt="Marie"
                                                        className="w-8 h-8 rounded-full object-cover"
                                                    />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                            Marie L.
                                                        </div>
                                                        <div className="text-[10px] text-gray-500 dark:text-grayDark-400">
                                                            2,450 XP
                                                        </div>
                                                    </div>
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="w-5 h-5 text-green-500"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            fill="currentColor"
                                                            d="M12 22c-4.714 0-7.071 0-8.536-1.465C2 19.072 2 16.714 2 12s0-7.071 1.464-8.536C4.93 2 7.286 2 12 2s7.071 0 8.535 1.464C22 4.93 22 7.286 22 12s0 7.071-1.465 8.535C19.072 22 16.714 22 12 22"
                                                            opacity=".5"
                                                        />
                                                        <path
                                                            fill="currentColor"
                                                            d="M16.03 8.97a.75.75 0 0 1 0 1.06l-5 5a.75.75 0 0 1-1.06 0l-2-2a.75.75 0 1 1 1.06-1.06l1.47 1.47l4.47-4.47a.75.75 0 0 1 1.06 0"
                                                        />
                                                    </svg>
                                                </div>
                                                <div className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-grayDark-700/50 rounded-lg">
                                                    <img
                                                        src="/images/site/people/man-1.jpg"
                                                        alt="Thomas"
                                                        className="w-8 h-8 rounded-full object-cover"
                                                    />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                            Thomas M.
                                                        </div>
                                                        <div className="text-[10px] text-gray-500 dark:text-grayDark-400">
                                                            1,890 XP
                                                        </div>
                                                    </div>
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="w-5 h-5 text-green-500"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            fill="currentColor"
                                                            d="M12 22c-4.714 0-7.071 0-8.536-1.465C2 19.072 2 16.714 2 12s0-7.071 1.464-8.536C4.93 2 7.286 2 12 2s7.071 0 8.535 1.464C22 4.93 22 7.286 22 12s0 7.071-1.465 8.535C19.072 22 16.714 22 12 22"
                                                            opacity=".5"
                                                        />
                                                        <path
                                                            fill="currentColor"
                                                            d="M16.03 8.97a.75.75 0 0 1 0 1.06l-5 5a.75.75 0 0 1-1.06 0l-2-2a.75.75 0 1 1 1.06-1.06l1.47 1.47l4.47-4.47a.75.75 0 0 1 1.06 0"
                                                        />
                                                    </svg>
                                                </div>
                                                <div className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-grayDark-700/50 rounded-lg">
                                                    <img
                                                        src="/images/site/people/woman-2.jpg"
                                                        alt="Sophie"
                                                        className="w-8 h-8 rounded-full object-cover"
                                                    />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                            Sophie D.
                                                        </div>
                                                        <div className="text-[10px] text-gray-500 dark:text-grayDark-400">
                                                            1,654 XP
                                                        </div>
                                                    </div>
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="w-5 h-5 text-purple-500"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            fill="currentColor"
                                                            d="M2.464 21.536C3.93 23 6.286 23 11 23s7.071 0 8.535-1.465C21 20.072 21 17.714 21 13s0-7.071-1.465-8.536C18.072 3 15.715 3 11 3S3.929 3 2.464 4.464C1 5.93 1 8.286 1 13s0 7.071 1.464 8.535"
                                                            opacity=".5"
                                                        />
                                                        <path
                                                            fill="currentColor"
                                                            fillRule="evenodd"
                                                            d="M16.375 4.65a.75.75 0 1 1-.75-1.3l3.464-2a.75.75 0 0 1 1.1.844l-.996 3.715l1.896-1.094a.75.75 0 1 1 .75 1.299l-3.464 2a.75.75 0 0 1-1.1-.844l.996-3.715zm-3.702 1.5a.75.75 0 1 1 .75-1.3l1.732 1a.75.75 0 0 1-.181 1.374l-.983.264l.164.095a.75.75 0 1 1-.75 1.299l-1.732-1a.75.75 0 0 1 .18-1.374l.984-.264z"
                                                            clipRule="evenodd"
                                                        />
                                                        <path
                                                            fill="currentColor"
                                                            d="M11 18a1 1 0 1 0 0-2a1 1 0 0 0 0 2m.999-6.558a.75.75 0 0 1 1.059.056c.465.517.965.752 1.442.752s.977-.235 1.442-.751a.75.75 0 1 1 1.116 1.002c-.67.744-1.548 1.249-2.558 1.249s-1.889-.505-2.558-1.248a.75.75 0 0 1 .056-1.06m-5.94.057A.75.75 0 1 0 4.942 12.5c.67.744 1.548 1.249 2.558 1.249s1.889-.505 2.558-1.248a.75.75 0 1 0-1.116-1.004c-.464.517-.965.752-1.442.752s-.977-.235-1.442-.751"
                                                        />
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Floating elements */}
                            <div className="absolute -top-4 -right-4 bg-white dark:bg-grayDark-800 rounded-xl shadow-lg p-3 border border-gray-200 dark:border-grayDark-700 hidden lg:block">
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
                                        <SolarIcon name="graph-up-linear" className="w-4 h-4 text-green-600 dark:text-green-400" />
                                    </div>
                                    <div>
                                        <div className="text-xs font-semibold text-gray-900 dark:text-white">
                                            {t("mockupGrowth")}
                                        </div>
                                        <div className="text-[10px] text-gray-500 dark:text-grayDark-400">
                                            {t("mockupThisMonth")}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="absolute -bottom-4 -left-4 bg-white dark:bg-grayDark-800 rounded-xl shadow-lg p-3 border border-gray-200 dark:border-grayDark-700 hidden lg:block">
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-4 h-4 text-primary"
                                            viewBox="0 0 24 24"
                                        >
                                            <g
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                            >
                                                <path d="M17 8V6c0-1.886 0-2.828-.586-3.414S14.886 2 13 2h-2c-1.886 0-2.828 0-3.414.586S7 4.114 7 6v2m4.146 4.023c.38-.682.57-1.023.854-1.023s.474.34.854 1.023l.098.176c.108.194.162.29.246.354c.085.064.19.088.4.135l.19.044c.738.167 1.107.25 1.195.532s-.164.577-.667 1.165l-.13.152c-.143.167-.215.25-.247.354s-.021.215 0 .438l.02.203c.076.785.114 1.178-.115 1.352c-.23.174-.576.015-1.267-.303l-.178-.082c-.197-.09-.295-.136-.399-.136s-.202.046-.399.136l-.178.082c-.691.319-1.037.477-1.267.303s-.191-.567-.115-1.352l.02-.203c.021-.223.032-.334 0-.438s-.104-.187-.247-.354l-.13-.152c-.503-.588-.755-.882-.667-1.165c.088-.282.457-.365 1.195-.532l.19-.044c.21-.047.315-.07.4-.135c.084-.064.138-.16.246-.354z" />
                                                <circle cx="12" cy="14" r="8" />
                                            </g>
                                        </svg>
                                    </div>
                                    <div>
                                        <div className="text-xs font-semibold text-gray-900 dark:text-white">
                                            {t("mockupBadgesCount")}
                                        </div>
                                        <div className="text-[10px] text-gray-500 dark:text-grayDark-400">
                                            {t("mockupUnlocked")}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 4 Main Features */}
            <section className="pt-12 sm:pt-20 pb-16 sm:pb-24 px-4 overflow-hidden">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-row gap-6 sm:gap-0">
                        {mainFeatures.map((feature, index) => (
                            <div
                                key={index}
                                className="group flex-1 relative px-4 sm:px-8 py-6 sm:py-12 lg:py-0"
                            >
                                {/* Vertical line separator */}
                                {index > 0 && (
                                    <div className="hidden lg:block absolute left-0 top-1/2 -translate-y-1/2 w-px h-24 bg-gradient-to-b from-transparent via-gray-300 dark:via-grayDark-700 to-transparent" />
                                )}
                                <div className="text-left">
                                    <div className="text-gray-900 dark:text-white w-6 h-6 sm:w-8 sm:h-8 mb-4 sm:mb-6">
                                        {feature.icon}
                                    </div>
                                    <h3 className="font-semibold text-gray-900 dark:text-white text-base sm:text-xl mb-2 sm:mb-4 tracking-tight">
                                        {feature.title}
                                    </h3>
                                    <p className="text-gray-500 dark:text-grayDark-400 text-xs sm:text-sm leading-relaxed">
                                        {feature.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonial Section - Full Width */}
            <section className="py-16 sm:py-24 px-4">
                <div className="max-w-7xl mx-auto">
                    <div className="grid xl:grid-cols-[1fr_320px] gap-10 sm:gap-16 xl:gap-24 items-start">
                        {/* Left - Quote */}
                        <div>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-8 h-8 sm:w-10 sm:h-10 text-primary mb-4 sm:mb-6"
                                viewBox="0 0 48 48"
                            >
                                <path
                                    fill="currentColor"
                                    fillRule="evenodd"
                                    d="M18.853 9.116Q7.558 16.37 6.303 26.003C5 36 13.94 40.893 18.47 36.497s1.815-9.977-1.465-11.503s-5.286-.994-4.936-3.033s5.016-7.69 9.116-10.322a.75.75 0 0 0 .114-1.02L20.285 9.3c-.44-.572-.862-.55-1.432-.185m19.826.001q-11.295 7.254-12.55 16.887C24.825 36 33.765 40.893 38.295 36.497s1.815-9.977-1.466-11.503s-5.286-.994-4.936-3.033s5.017-7.69 9.117-10.322a.75.75 0 0 0 .113-1.02L40.11 9.3c-.44-.572-.862-.55-1.431-.185"
                                    clipRule="evenodd"
                                />
                            </svg>
                            <div className="relative h-[220px] sm:h-[180px] lg:h-[160px]">
                                <AnimatePresence mode="wait">
                                    <motion.blockquote
                                        key={activeIndustry}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.15 }}
                                        className="text-xl sm:text-2xl lg:text-3xl font-normal text-gray-900 dark:text-white leading-snug absolute inset-0"
                                    >
                                        <span className="underline decoration-primary decoration-2 underline-offset-4">
                                            {currentTestimonial.highlight}
                                        </span>
                                        {currentTestimonial.quote.substring(
                                            currentTestimonial.quote.indexOf(
                                                currentTestimonial.highlight,
                                            ) +
                                                currentTestimonial.highlight
                                                    .length,
                                        )}
                                    </motion.blockquote>
                                </AnimatePresence>
                            </div>
                        </div>

                        {/* Right - Tabs + Company + Author */}
                        <div className="flex flex-col items-center xl:items-end">
                            {/* Industry Tabs */}
                            <div className="flex items-center flex-wrap justify-center xl:justify-end gap-1 text-xs sm:text-sm mb-8 sm:mb-12">
                                {industries.map((industry, index) => (
                                    <span
                                        key={industry.id}
                                        className="flex items-center"
                                    >
                                        <button
                                            onClick={() =>
                                                setActiveIndustry(industry.id)
                                            }
                                            className={`transition-colors duration-150 ${
                                                activeIndustry === industry.id
                                                    ? "text-primary font-medium"
                                                    : "text-gray-400 dark:text-grayDark-500 hover:text-gray-600 dark:hover:text-grayDark-300"
                                            }`}
                                        >
                                            {industry.label}
                                        </button>
                                        {index < industries.length - 1 && (
                                            <span className="text-gray-300 dark:text-grayDark-600 mx-2">
                                                /
                                            </span>
                                        )}
                                    </span>
                                ))}
                            </div>

                            {/* Company + Author */}
                            <div className="relative h-[100px] w-full">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={activeIndustry}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.15 }}
                                        className="absolute inset-0 flex flex-col items-center xl:items-end"
                                    >
                                        <div className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white tracking-tight mb-5 text-center xl:text-right">
                                            {currentTestimonial.company}.
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 dark:from-grayDark-700 dark:to-grayDark-800 flex items-center justify-center overflow-hidden">
                                                <span className="text-gray-600 dark:text-grayDark-300 font-medium text-xs">
                                                    {currentTestimonial.avatar}
                                                </span>
                                            </div>
                                            <div>
                                                <div className="font-medium text-gray-900 dark:text-white text-sm">
                                                    {currentTestimonial.author}
                                                </div>
                                                <div className="text-gray-500 dark:text-grayDark-400 text-sm">
                                                    {currentTestimonial.role}
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                </AnimatePresence>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Section - Large Premium Numbers */}
            <section className="py-12 sm:py-20 px-4">
                <div className="max-w-7xl mx-auto px-4 sm:px-8">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-16 lg:gap-24">
                        {stats.map((stat, index) => (
                            <div key={index} className="text-left">
                                <div className="text-4xl sm:text-5xl lg:text-7xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4 tracking-tight">
                                    {stat.value}
                                </div>
                                <div className="text-gray-500 dark:text-grayDark-400 text-xs sm:text-sm leading-relaxed max-w-xs">
                                    {stat.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Feature 1: Skilled teams */}
            <section className="py-16 sm:py-24 px-4">
                <div className="max-w-6xl mx-auto">
                    <div className="grid xl:grid-cols-2 gap-10 xl:gap-16 items-center">
                        <div>
                            <p className="text-xs sm:text-sm font-semibold text-primary mb-3 sm:mb-4 tracking-wide">
                                {t("expertiseTag")}
                            </p>
                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-3 leading-tight">
                                {t("expertiseTitle")}
                            </h2>
                            <p className="text-base sm:text-xl text-gray-500 dark:text-grayDark-400 mb-8 sm:mb-10 leading-relaxed">
                                {t("expertiseSubtitle")}
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                                {expertisePoints.map((point, i) => (
                                    <div key={i} className="flex gap-3">
                                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gray-100 dark:bg-grayDark-800 flex items-center justify-center text-gray-700 dark:text-grayDark-300 flex-shrink-0">
                                            {point.icon}
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-900 dark:text-white mb-1 text-sm sm:text-base">
                                                {point.title}
                                            </p>
                                            <p className="text-xs sm:text-sm text-gray-500 dark:text-grayDark-400 mb-0">
                                                {point.desc}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {/* Image placeholder */}
                        <div className="bg-gradient-to-br from-gray-100 to-gray-50 dark:from-grayDark-800 dark:to-grayDark-900 rounded-3xl p-4 sm:p-8 aspect-[1/1] flex items-center justify-center">
                            <div className="bg-white dark:bg-grayDark-800 rounded-2xl shadow-xl border border-gray-200 dark:border-grayDark-700 w-full h-full p-6 flex flex-col">
                                {/* Header with search */}
                                <div className="flex items-center justify-between mb-6">
                                    <div className="font-semibold text-gray-900 dark:text-white">
                                        {t("mockupCatalogue")}
                                    </div>
                                    <div className="flex items-center gap-2 bg-gray-100 dark:bg-grayDark-700 rounded-lg px-3 py-2">
                                        <SolarIcon name="magnifer-linear" className="w-4 h-4 text-gray-400" />
                                        <span className="text-sm text-gray-400">
                                            {t("mockupSearch")}
                                        </span>
                                    </div>
                                </div>
                                {/* Categories */}
                                <div className="flex gap-2 mb-6">
                                    <span className="px-3 py-1.5 bg-primary text-white text-xs font-medium rounded-full">
                                        {t("mockupAll")}
                                    </span>
                                    <span className="px-3 py-1.5 bg-gray-100 dark:bg-grayDark-700 text-gray-600 dark:text-grayDark-300 text-xs font-medium rounded-full">
                                        {t("mockupTech")}
                                    </span>
                                    <span className="px-3 py-1.5 bg-gray-100 dark:bg-grayDark-700 text-gray-600 dark:text-grayDark-300 text-xs font-medium rounded-full">
                                        {t("mockupDesign")}
                                    </span>
                                    <span className="px-3 py-1.5 bg-gray-100 dark:bg-grayDark-700 text-gray-600 dark:text-grayDark-300 text-xs font-medium rounded-full">
                                        {t("mockupBusiness")}
                                    </span>
                                </div>
                                {/* Course cards */}
                                <div className="grid grid-cols-2 gap-4 flex-1">
                                    <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-4 flex flex-col justify-between text-white">
                                        <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mb-auto">
                                            <img
                                                src="/images/site/courses/icon-react.png"
                                                alt="React"
                                                className="w-10 h-10 mb-1"
                                            />
                                        </div>
                                        <div>
                                            <div className="font-medium text-sm mb-1">
                                                {t("mockupReactAdvanced")}
                                            </div>
                                            <div className="text-xs text-white/70">
                                                {t("mockupReactSessions")}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl p-4 flex flex-col justify-between text-white">
                                        <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mb-auto">
                                            <img
                                                src="/images/site/courses/icon-figma.png"
                                                alt="Figma"
                                                className="w-10 h-10 mb-1"
                                            />
                                        </div>
                                        <div>
                                            <div className="font-medium text-sm mb-1">
                                                {t("mockupUxDesign")}
                                            </div>
                                            <div className="text-xs text-white/70">
                                                {t("mockupUxSessions")}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-4 flex flex-col justify-between text-white">
                                        <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mb-auto">
                                            <img
                                                src="/images/site/courses/icon-jira.png"
                                                alt="Jira"
                                                className="w-10 h-10 mb-1"
                                            />
                                        </div>
                                        <div>
                                            <div className="font-medium text-sm mb-1">
                                                {t("mockupProductMgmt")}
                                            </div>
                                            <div className="text-xs text-white/70">
                                                {t("mockupProductSessions")}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-4 flex flex-col justify-between text-white">
                                        <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mb-auto">
                                            <img
                                                src="/images/site/courses/icon-claudecode.png"
                                                alt="Claude Code"
                                                className="w-10 h-10 mb-1"
                                            />
                                        </div>
                                        <div>
                                            <div className="font-medium text-sm mb-1">
                                                {t("mockupExpertAI")}
                                            </div>
                                            <div className="text-xs text-white/70">
                                                {t("mockupAISessions")}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Feature 2: Upskill fast */}
            <section className="py-16 sm:py-24 px-4">
                <div className="max-w-6xl mx-auto">
                    <div className="grid xl:grid-cols-2 gap-10 xl:gap-16 items-center">
                        {/* Image placeholder - LEFT */}
                        <div className="order-2 xl:order-1 bg-white dark:bg-grayDark-900 rounded-3xl p-4 sm:p-8 aspect-[1/1] flex items-center justify-center shadow-sm dark:shadow-none border border-gray-200 dark:border-grayDark-800">
                            <div className="w-full h-full flex flex-col gap-4">
                                {/* XP Progress card */}
                                <div className="bg-gradient-to-r from-primary/10 to-purple-500/10 dark:from-primary/20 dark:to-purple-500/20 rounded-xl p-5 border border-primary/20">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-primary/20 dark:bg-primary/30 rounded-full flex items-center justify-center">
                                                <SolarIcon name="bolt-linear" className="w-5 h-5 text-primary" />
                                            </div>
                                            <div>
                                                <div className="font-semibold text-gray-900 dark:text-white text-sm">
                                                    2,450 XP
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-grayDark-400">
                                                    {t("mockupThisWeek")}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-green-500 text-sm font-medium">
                                            +340 XP
                                        </div>
                                    </div>
                                    <div className="h-2 bg-gray-200 dark:bg-grayDark-700 rounded-full overflow-hidden">
                                        <div className="h-full w-3/4 bg-gradient-to-r from-primary to-purple-500 rounded-full" />
                                    </div>
                                </div>
                                {/* Badges row */}
                                <div className="bg-gray-50 dark:bg-grayDark-800 rounded-xl p-5 border border-gray-200 dark:border-grayDark-700">
                                    <div className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                                        {t("mockupRecentBadges")}
                                    </div>
                                    <div className="flex gap-3">
                                        <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-xl flex items-center justify-center">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="w-6 h-6 text-yellow-500"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    fill="currentColor"
                                                    d="M9.153 5.408C10.42 3.136 11.053 2 12 2s1.58 1.136 2.847 3.408l.328.588c.36.646.54.969.82 1.182c.28.213.63.292 1.33.45l.636.144c2.46.557 3.689.835 3.982 1.776c.292.94-.546 1.921-2.223 3.882l-.434.507c-.476.557-.715.836-.822 1.18c-.107.345-.071.717.001 1.46l.066.677c.253 2.617.38 3.925-.386 4.506s-1.918.052-4.22-1.009l-.597-.274c-.654-.302-.981-.452-1.328-.452s-.674.15-1.328.452l-.596.274c-2.303 1.06-3.455 1.59-4.22 1.009c-.767-.581-.64-1.89-.387-4.506l.066-.676c.072-.744.108-1.116 0-1.46c-.106-.345-.345-.624-.821-1.18l-.434-.508c-1.677-1.96-2.515-2.941-2.223-3.882c.293-.941 1.523-1.22 3.983-1.776l.636-.144c.699-.158 1.048-.237 1.329-.45c.28-.213.46-.536.82-1.182z"
                                                />
                                            </svg>
                                        </div>
                                        <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="w-6 h-6 text-blue-500"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    fill="currentColor"
                                                    d="M9.153 5.408C10.42 3.136 11.053 2 12 2s1.58 1.136 2.847 3.408l.328.588c.36.646.54.969.82 1.182c.28.213.63.292 1.33.45l.636.144c2.46.557 3.689.835 3.982 1.776c.292.94-.546 1.921-2.223 3.882l-.434.507c-.476.557-.715.836-.822 1.18c-.107.345-.071.717.001 1.46l.066.677c.253 2.617.38 3.925-.386 4.506s-1.918.052-4.22-1.009l-.597-.274c-.654-.302-.981-.452-1.328-.452s-.674.15-1.328.452l-.596.274c-2.303 1.06-3.455 1.59-4.22 1.009c-.767-.581-.64-1.89-.387-4.506l.066-.676c.072-.744.108-1.116 0-1.46c-.106-.345-.345-.624-.821-1.18l-.434-.508c-1.677-1.96-2.515-2.941-2.223-3.882c.293-.941 1.523-1.22 3.983-1.776l.636-.144c.699-.158 1.048-.237 1.329-.45c.28-.213.46-.536.82-1.182z"
                                                />
                                            </svg>
                                        </div>
                                        <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="w-6 h-6 text-purple-500"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    fill="currentColor"
                                                    d="M9.153 5.408C10.42 3.136 11.053 2 12 2s1.58 1.136 2.847 3.408l.328.588c.36.646.54.969.82 1.182c.28.213.63.292 1.33.45l.636.144c2.46.557 3.689.835 3.982 1.776c.292.94-.546 1.921-2.223 3.882l-.434.507c-.476.557-.715.836-.822 1.18c-.107.345-.071.717.001 1.46l.066.677c.253 2.617.38 3.925-.386 4.506s-1.918.052-4.22-1.009l-.597-.274c-.654-.302-.981-.452-1.328-.452s-.674.15-1.328.452l-.596.274c-2.303 1.06-3.455 1.59-4.22 1.009c-.767-.581-.64-1.89-.387-4.506l.066-.676c.072-.744.108-1.116 0-1.46c-.106-.345-.345-.624-.821-1.18l-.434-.508c-1.677-1.96-2.515-2.941-2.223-3.882c.293-.941 1.523-1.22 3.983-1.776l.636-.144c.699-.158 1.048-.237 1.329-.45c.28-.213.46-.536.82-1.182z"
                                                />
                                            </svg>
                                        </div>
                                        <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="w-6 h-6 text-green-500"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    fill="currentColor"
                                                    d="M9.153 5.408C10.42 3.136 11.053 2 12 2s1.58 1.136 2.847 3.408l.328.588c.36.646.54.969.82 1.182c.28.213.63.292 1.33.45l.636.144c2.46.557 3.689.835 3.982 1.776c.292.94-.546 1.921-2.223 3.882l-.434.507c-.476.557-.715.836-.822 1.18c-.107.345-.071.717.001 1.46l.066.677c.253 2.617.38 3.925-.386 4.506s-1.918.052-4.22-1.009l-.597-.274c-.654-.302-.981-.452-1.328-.452s-.674.15-1.328.452l-.596.274c-2.303 1.06-3.455 1.59-4.22 1.009c-.767-.581-.64-1.89-.387-4.506l.066-.676c.072-.744.108-1.116 0-1.46c-.106-.345-.345-.624-.821-1.18l-.434-.508c-1.677-1.96-2.515-2.941-2.223-3.882c.293-.941 1.523-1.22 3.983-1.776l.636-.144c.699-.158 1.048-.237 1.329-.45c.28-.213.46-.536.82-1.182z"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                                {/* League card */}
                                <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl p-5 border border-amber-200 dark:border-amber-800/50 flex-1">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="font-semibold text-gray-900 dark:text-white">
                                            League Or
                                        </div>
                                        <SolarIcon name="cup-star-linear" className="w-5 h-5 text-amber-500" />
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-3">
                                            <span className="text-sm font-medium text-amber-600 dark:text-amber-400 w-5">
                                                1
                                            </span>
                                            <img
                                                src="/images/site/people/woman-1.jpg"
                                                alt="Marie"
                                                className="w-6 h-6 rounded-full object-cover"
                                            />
                                            <span className="text-sm text-gray-700 dark:text-grayDark-300 flex-1">
                                                Marie L.
                                            </span>
                                            <span className="text-xs text-gray-500 dark:text-grayDark-400">
                                                2,450 XP
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="text-sm font-medium text-gray-400 w-5">
                                                2
                                            </span>
                                            <img
                                                src="/images/site/people/man-1.jpg"
                                                alt="Thomas"
                                                className="w-6 h-6 rounded-full object-cover"
                                            />
                                            <span className="text-sm text-gray-700 dark:text-grayDark-300 flex-1">
                                                Thomas M.
                                            </span>
                                            <span className="text-xs text-gray-500 dark:text-grayDark-400">
                                                1,890 XP
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="text-sm font-medium text-gray-400 w-5">
                                                3
                                            </span>
                                            <img
                                                src="/images/site/people/woman-2.jpg"
                                                alt="Sophie"
                                                className="w-6 h-6 rounded-full object-cover"
                                            />
                                            <span className="text-sm text-gray-700 dark:text-grayDark-300 flex-1">
                                                Sophie D.
                                            </span>
                                            <span className="text-xs text-gray-500 dark:text-grayDark-400">
                                                1,654 XP
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="order-1 xl:order-2">
                            <p className="text-xs sm:text-sm font-semibold text-primary mb-3 sm:mb-4 tracking-wide">
                                {t("upskillingTag")}
                            </p>
                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-3 leading-tight">
                                {t("upskillingTitle")}
                            </h2>
                            <p className="text-base sm:text-xl text-gray-500 dark:text-grayDark-400 mb-8 sm:mb-10 leading-relaxed">
                                {t("upskillingSubtitle")}
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                                {upskillingPoints.map((point, i) => (
                                    <div key={i} className="flex gap-3">
                                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gray-100 dark:bg-grayDark-800 flex items-center justify-center text-gray-700 dark:text-grayDark-300 flex-shrink-0">
                                            {point.icon}
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-900 dark:text-white mb-1 text-sm sm:text-base">
                                                {point.title}
                                            </p>
                                            <p className="text-xs sm:text-sm text-gray-500 dark:text-grayDark-400 mb-0">
                                                {point.desc}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Feature 3: See progress */}
            <section className="py-16 sm:py-24 px-4">
                <div className="max-w-6xl mx-auto">
                    <div className="grid xl:grid-cols-2 gap-10 xl:gap-16 items-center">
                        <div>
                            <p className="text-xs sm:text-sm font-semibold text-primary mb-3 sm:mb-4 tracking-wide">
                                {t("trackingTag")}
                            </p>
                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-3 leading-tight">
                                {t("trackingTitle")}
                            </h2>
                            <p className="text-base sm:text-xl text-gray-500 dark:text-grayDark-400 mb-8 sm:mb-10 leading-relaxed">
                                {t("trackingSubtitle")}
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                                {trackingPoints.map((point, i) => (
                                    <div key={i} className="flex gap-3">
                                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gray-100 dark:bg-grayDark-800 flex items-center justify-center text-gray-700 dark:text-grayDark-300 flex-shrink-0">
                                            {point.icon}
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-900 dark:text-white mb-1 text-sm sm:text-base">
                                                {point.title}
                                            </p>
                                            <p className="text-xs sm:text-sm text-gray-500 dark:text-grayDark-400 mb-0">
                                                {point.desc}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {/* Image placeholder */}
                        <div className="bg-gradient-to-br from-gray-100 to-gray-50 dark:from-grayDark-800 dark:to-grayDark-900 rounded-3xl p-4 sm:p-8 aspect-[1/1] flex items-center justify-center">
                            <div className="bg-white dark:bg-grayDark-800 rounded-2xl shadow-xl border border-gray-200 dark:border-grayDark-700 w-full h-full p-6 flex flex-col">
                                {/* Header */}
                                <div className="flex items-center justify-between mb-6">
                                    <div className="font-semibold text-gray-900 dark:text-white">
                                        {t("mockupAnalytics")}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-500 dark:text-grayDark-400">
                                            {t("mockupThisMonthDropdown")}
                                        </span>
                                        <SolarIcon name="alt-arrow-down-linear" className="w-4 h-4 text-gray-400" />
                                    </div>
                                </div>
                                {/* KPI Cards */}
                                <div className="grid grid-cols-3 gap-3 mb-5">
                                    <div className="bg-green-50 dark:bg-green-900/30 rounded-xl p-3 text-center">
                                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                                            94%
                                        </div>
                                        <div className="text-[10px] text-gray-500 dark:text-grayDark-400 mt-0.5">
                                            {t("mockupCompletion")}
                                        </div>
                                        <div className="text-[10px] text-green-500 mt-1">
                                            ↑ 12%
                                        </div>
                                    </div>
                                    <div className="bg-primary/5 dark:bg-primary/20 rounded-xl p-3 text-center">
                                        <div className="text-2xl font-bold text-primary">
                                            2.4K
                                        </div>
                                        <div className="text-[10px] text-gray-500 dark:text-grayDark-400 mt-0.5">
                                            {t("mockupHours")}
                                        </div>
                                        <div className="text-[10px] text-green-500 mt-1">
                                            ↑ 8%
                                        </div>
                                    </div>
                                    <div className="bg-purple-50 dark:bg-purple-900/30 rounded-xl p-3 text-center">
                                        <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                                            47/50
                                        </div>
                                        <div className="text-[10px] text-gray-500 dark:text-grayDark-400 mt-0.5">
                                            {t("mockupActive")}
                                        </div>
                                        <div className="text-[10px] text-green-500 mt-1">
                                            ↑ 4
                                        </div>
                                    </div>
                                </div>
                                {/* Chart */}
                                <div className="bg-gray-50 dark:bg-grayDark-700/50 rounded-xl p-4 flex-1 flex flex-col">
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-xs font-medium text-gray-700 dark:text-grayDark-300">
                                            {t("mockupMonthlyProgress")}
                                        </span>
                                        <span className="text-xs text-green-500 font-medium">
                                            {t("mockupVsLastMonth")}
                                        </span>
                                    </div>
                                    <div className="flex items-end gap-1.5 flex-1">
                                        {[
                                            35, 42, 38, 55, 48, 62, 58, 70, 65,
                                            78, 72, 85,
                                        ].map((h, i) => (
                                            <div
                                                key={i}
                                                className="flex-1 bg-gradient-to-t from-primary to-primary/60 rounded-t transition-all hover:from-primary hover:to-primary"
                                                style={{ height: `${h}%` }}
                                            />
                                        ))}
                                    </div>
                                    <div className="flex justify-between mt-2 text-[10px] text-gray-400">
                                        <span>{t("mockupJan")}</span>
                                        <span>{t("mockupFeb")}</span>
                                        <span>{t("mockupMar")}</span>
                                        <span>{t("mockupApr")}</span>
                                        <span>{t("mockupMay")}</span>
                                        <span>{t("mockupJun")}</span>
                                        <span>{t("mockupJul")}</span>
                                        <span>{t("mockupAug")}</span>
                                        <span>{t("mockupSep")}</span>
                                        <span>{t("mockupOct")}</span>
                                        <span>{t("mockupNov")}</span>
                                        <span>{t("mockupDec")}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Certification Section */}
            <section className="py-16 sm:py-24 px-4">
                <div className="max-w-6xl mx-auto">
                    <div className="grid xl:grid-cols-2 gap-10 xl:gap-16 items-center">
                        {/* Image placeholder - LEFT */}
                        <div className="bg-gradient-to-br from-gray-100 to-gray-50 dark:from-grayDark-800 dark:to-grayDark-900 rounded-3xl p-4 sm:p-8 aspect-[1/1] flex items-center justify-center order-2 xl:order-1">
                            <div className="bg-white dark:bg-grayDark-800 rounded-2xl shadow-xl border border-gray-200 dark:border-grayDark-700 w-full h-full p-4 sm:p-6 flex flex-col">
                                {/* Header */}
                                <div className="flex items-center justify-between mb-6">
                                    <div className="font-semibold text-gray-900 dark:text-white">
                                        {t("mockupCertifications")}
                                    </div>
                                    <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-1 rounded-full font-medium">
                                        {t("mockupObtained")}
                                    </span>
                                </div>
                                {/* Certificate cards */}
                                <div className="space-y-3 flex-1">
                                    {/* Certificate 1 - Completed */}
                                    <div className="bg-gradient-to-r from-primary/10 to-purple-500/10 dark:from-primary/20 dark:to-purple-500/20 rounded-xl p-4 border border-primary/20">
                                        <div className="flex items-start gap-4">
                                            <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
                                                <img
                                                    src="/images/site/courses/icon-react.png"
                                                    alt="React"
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="font-medium text-gray-900 dark:text-white text-sm">
                                                        {t("mockupReactDeveloper")}
                                                    </span>
                                                    <SolarIcon name="check-circle-linear" className="w-4 h-4 text-green-500" />
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-grayDark-400 mb-2">
                                                    {t("mockupObtainedDate1")}
                                                </div>
                                                <div className="h-1.5 bg-primary rounded-full w-full" />
                                            </div>
                                        </div>
                                    </div>
                                    {/* Certificate 2 - Completed */}
                                    <div className="bg-gray-50 dark:bg-grayDark-700/50 rounded-xl p-4 border border-gray-200 dark:border-grayDark-600">
                                        <div className="flex items-start gap-4">
                                            <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
                                                <img
                                                    src="/images/site/courses/icon-figma.png"
                                                    alt="Figma"
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="font-medium text-gray-900 dark:text-white text-sm">
                                                        {t("mockupUxDesignFundamentals")}
                                                    </span>
                                                    <SolarIcon name="check-circle-linear" className="w-4 h-4 text-green-500" />
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-grayDark-400 mb-2">
                                                    {t("mockupObtainedDate2")}
                                                </div>
                                                <div className="h-1.5 bg-emerald-500 rounded-full w-full" />
                                            </div>
                                        </div>
                                    </div>
                                    {/* Certificate 3 - In progress */}
                                    <div className="bg-gray-50 dark:bg-grayDark-700/50 rounded-xl p-4 border border-gray-200 dark:border-grayDark-600">
                                        <div className="flex items-start gap-4">
                                            <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
                                                <img
                                                    src="/images/site/courses/icon-jira.png"
                                                    alt="Jira"
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="font-medium text-gray-900 dark:text-white text-sm">
                                                        {t("mockupProductManagement")}
                                                    </span>
                                                    <span className="text-[10px] bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded font-medium">
                                                        {t("mockupInProgress")}
                                                    </span>
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-grayDark-400 mb-2">
                                                    {t("mockupCompleted")}
                                                </div>
                                                <div className="h-1.5 bg-gray-200 dark:bg-grayDark-600 rounded-full w-full overflow-hidden">
                                                    <div className="h-full w-2/3 bg-amber-500 rounded-full" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="order-1 xl:order-2">
                            <p className="text-xs sm:text-sm font-semibold text-primary mb-3 sm:mb-4 tracking-wide">
                                {t("certificationTag")}
                            </p>
                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-3 leading-tight">
                                {t("certificationTitle")}
                            </h2>
                            <p className="text-base sm:text-xl text-gray-500 dark:text-grayDark-400 mb-8 sm:mb-10 leading-relaxed">
                                {t("certificationSubtitle")}
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
                                {certificationPoints.map((point, i) => (
                                    <div key={i} className="flex gap-3">
                                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gray-100 dark:bg-grayDark-800 flex items-center justify-center text-gray-700 dark:text-grayDark-300 flex-shrink-0">
                                            {point.icon}
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-900 dark:text-white mb-1 text-sm sm:text-base">
                                                {point.title}
                                            </p>
                                            <p className="text-xs sm:text-sm text-gray-500 dark:text-grayDark-400 mb-0">
                                                {point.desc}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <Link
                                href={`/${locale}/certifications`}
                                className="inline-flex items-center gap-2 text-primary font-medium hover:underline text-sm sm:text-base"
                            >
                                {t("learnMore")}{" "}
                                <SolarIcon name="arrow-right-linear" className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Skill Mapping Section - COMMENTED FOR NOW
            <section className="py-24 px-4">
                <div className="max-w-6xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <p className="text-sm font-semibold text-primary mb-4 tracking-wide">
                                SKILL MAPPING
                            </p>
                            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-3 leading-tight">
                                Cartographiez les compétences en minutes
                            </h2>
                            <p className="text-xl text-gray-500 dark:text-grayDark-400 mb-10 leading-relaxed">
                                Vision claire des capacités de l'équipe.
                                Identifiez, planifiez, progressez.
                            </p>
                            <div className="grid grid-cols-2 gap-6 mb-8">
                                {skillMappingPoints.map((point, i) => (
                                    <div key={i} className="flex gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-grayDark-800 flex items-center justify-center text-gray-700 dark:text-grayDark-300 flex-shrink-0">
                                            {point.icon}
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-900 dark:text-white mb-1">
                                                {point.title}
                                            </p>
                                            <p className="text-sm text-gray-500 dark:text-grayDark-400 mb-0">
                                                {point.desc}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <Link
                                href={`/${locale}/features/skill-mapping`}
                                className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
                            >
                                {t("learnMore")}{" "}
                                <SolarIcon name="arrow-right-linear" className="w-4 h-4" />
                            </Link>
                        </div>
                        <div className="bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 rounded-3xl p-8 aspect-[1/1] flex items-center justify-center border border-cyan-200/50 dark:border-cyan-800/30">
                            <div className="bg-white dark:bg-grayDark-800 rounded-2xl shadow-xl border border-gray-200 dark:border-grayDark-700 w-full h-full p-6 flex items-center justify-center">
                                <div className="relative w-64 h-64">
                                    <svg
                                        viewBox="0 0 100 100"
                                        className="w-full h-full"
                                    >
                                        <polygon
                                            points="50,5 95,27.5 82,77.5 18,77.5 5,27.5"
                                            fill="none"
                                            className="stroke-gray-200 dark:stroke-grayDark-600"
                                            strokeWidth="1"
                                        />
                                        <polygon
                                            points="50,20 80,35 70,70 30,70 20,35"
                                            fill="none"
                                            className="stroke-gray-200 dark:stroke-grayDark-600"
                                            strokeWidth="1"
                                        />
                                        <polygon
                                            points="50,35 65,45 60,62 40,62 35,45"
                                            fill="none"
                                            className="stroke-gray-200 dark:stroke-grayDark-600"
                                            strokeWidth="1"
                                        />
                                        <polygon
                                            points="50,12 88,30 76,74 24,74 12,30"
                                            fill="rgba(99, 102, 241, 0.15)"
                                            stroke="#6366f1"
                                            strokeWidth="2"
                                        />
                                    </svg>
                                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-3 text-sm font-medium text-gray-700 dark:text-grayDark-300">
                                        React
                                    </div>
                                    <div className="absolute top-1/4 -right-4 text-sm font-medium text-gray-700 dark:text-grayDark-300">
                                        TypeScript
                                    </div>
                                    <div className="absolute bottom-1/4 -right-4 text-sm font-medium text-gray-700 dark:text-grayDark-300">
                                        Node.js
                                    </div>
                                    <div className="absolute bottom-1/4 -left-4 text-sm font-medium text-gray-700 dark:text-grayDark-300">
                                        Design
                                    </div>
                                    <div className="absolute top-1/4 -left-4 text-sm font-medium text-gray-700 dark:text-grayDark-300">
                                        Product
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            */}

            {/* Custom Learning Paths - COMMENTED FOR NOW
            <section className="py-24 px-4">
                <div className="max-w-6xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div className="bg-white dark:bg-grayDark-900 rounded-3xl p-8 aspect-[1/1] flex items-center justify-center shadow-sm dark:shadow-none border border-gray-200 dark:border-grayDark-800">
                            <div className="w-full space-y-4">
                                <div className="h-8 w-36 bg-gray-100 dark:bg-grayDark-700 rounded-lg mb-6" />
                                {[1, 2, 3, 4].map((i) => (
                                    <div
                                        key={i}
                                        className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 dark:bg-grayDark-800"
                                    >
                                        <div
                                            className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                                                i <= 2
                                                    ? "bg-green-500"
                                                    : i === 3
                                                    ? "bg-primary"
                                                    : "bg-gray-300 dark:bg-grayDark-600"
                                            }`}
                                        >
                                            {i <= 2 ? "✓" : i}
                                        </div>
                                        <div className="flex-1">
                                            <div className="h-4 bg-gray-200 dark:bg-grayDark-600 rounded w-32 mb-2" />
                                            <div className="h-3 bg-gray-100 dark:bg-grayDark-700 rounded w-24" />
                                        </div>
                                        <div className="w-20 h-2 bg-gray-200 dark:bg-grayDark-700 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-primary rounded-full"
                                                style={{
                                                    width:
                                                        i <= 2
                                                            ? "100%"
                                                            : i === 3
                                                            ? "60%"
                                                            : "0%",
                                                }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-primary mb-4 tracking-wide">
                                PARCOURS PERSONNALISÉS
                            </p>
                            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-3 leading-tight">
                                Adaptez l'apprentissage à votre équipe
                            </h2>
                            <p className="text-xl text-gray-500 dark:text-grayDark-400 mb-10 leading-relaxed">
                                Créez des parcours sur mesure et laissez l'IA
                                personnaliser l'expérience pour chaque membre.
                            </p>
                            <div className="grid grid-cols-2 gap-6">
                                {customPathsPoints.map((point, i) => (
                                    <div key={i} className="flex gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-grayDark-800 flex items-center justify-center text-gray-700 dark:text-grayDark-300 flex-shrink-0">
                                            {point.icon}
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-900 dark:text-white mb-1">
                                                {point.title}
                                            </p>
                                            <p className="text-sm text-gray-500 dark:text-grayDark-400 mb-0">
                                                {point.desc}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            */}

            {/* Comparison Section - Uxcel Style */}
            <section className="py-16 sm:py-24 px-4">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-10 sm:mb-16">
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white">
                            {t("whatMakesBelievemyDifferent")}
                        </h2>
                    </div>

                    <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* VS Badge */}
                        <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                            <div className="w-14 h-14 rounded-full bg-white dark:bg-grayDark-800 border-2 border-gray-200 dark:border-grayDark-700 flex items-center justify-center">
                                <span className="text-primary font-bold text-sm">
                                    VS
                                </span>
                            </div>
                        </div>

                        {/* Left Card - Traditional */}
                        <div className="bg-white dark:bg-grayDark-900 rounded-2xl sm:rounded-3xl border-2 border-dashed border-gray-200 dark:border-grayDark-700 p-5 sm:p-8">
                            {/* App Icons with wavy dashed lines */}
                            <div className="relative flex items-center justify-center mb-8 h-20">
                                <div className="relative w-full h-32">
                                    {/* Wavy dashed path */}
                                    <svg
                                        className="absolute inset-0 w-full h-full"
                                        viewBox="0 0 400 100"
                                        preserveAspectRatio="xMidYMid meet"
                                    >
                                        <path
                                            d="M-20,50 C30,10 70,10 110,50 C150,90 190,90 230,50 C270,10 310,10 350,50 C390,90 430,90 470,50"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeDasharray="6 6"
                                            className="text-gray-200 dark:text-grayDark-700"
                                        />
                                    </svg>

                                    {/* App icons positioned along the wave */}
                                    {/* Udemy */}
                                    <div className="absolute left-[10%] top-[15%] w-11 h-11 rounded-xl flex items-center justify-center shadow-lg transform -rotate-6 overflow-hidden">
                                        <img
                                            src="/images/site/courses/icon-udemy.png"
                                            alt="Udemy"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    {/* Stackoverflow */}
                                    <div className="absolute left-[32%] top-[45%] w-11 h-11 rounded-xl flex items-center justify-center shadow-lg transform rotate-3 overflow-hidden">
                                        <img
                                            src="/images/site/courses/icon-stackoverflow.png"
                                            alt="Stack Overflow"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    {/* Coursera */}
                                    <div className="absolute left-[55%] top-[10%] w-11 h-11 rounded-xl flex items-center justify-center shadow-lg transform -rotate-3 overflow-hidden">
                                        <img
                                            src="/images/site/courses/icon-coursera.png"
                                            alt="Coursera"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    {/* YouTube - official logo */}
                                    <div className="absolute left-[78%] top-[40%] w-11 h-11 flex items-center justify-center shadow-lg transform rotate-6 rounded-xl overflow-hidden">
                                        <img
                                            src="/images/site/courses/icon-youtube.png"
                                            alt="YouTube"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                </div>
                            </div>

                            <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3">
                                {t("comparisonOldTitle")}
                            </h3>
                            <p className="text-gray-500 dark:text-grayDark-400 text-xs sm:text-sm mb-6 sm:mb-8">
                                {t("comparisonOldSubtitle")}
                            </p>

                            <div className="space-y-3 sm:space-y-4">
                                {[
                                    t("comparisonOldPoint1"),
                                    t("comparisonOldPoint2"),
                                    t("comparisonOldPoint3"),
                                    t("comparisonOldPoint4"),
                                    t("comparisonOldPoint5"),
                                    t("comparisonOldPoint6"),
                                ].map((item, i) => (
                                    <div
                                        key={i}
                                        className="flex items-start gap-2 sm:gap-3"
                                    >
                                        <SolarIcon name="dislike-linear" className="w-4 h-4 sm:w-5 sm:h-5 text-gray-300 dark:text-grayDark-600 flex-shrink-0 mt-0.5" />
                                        <span className="text-gray-500 dark:text-grayDark-400 text-xs sm:text-sm">
                                            {item}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Right Card - Believemy */}
                        <div className="bg-white dark:bg-grayDark-900 rounded-2xl sm:rounded-3xl border-2 border-dashed border-gray-200 dark:border-grayDark-700 p-5 sm:p-8">
                            {/* Believemy Logo/Icon with orbital circles */}
                            <div className="flex items-center justify-center mb-8 relative h-20 overflow-hidden">
                                <div className="relative w-80 h-80">
                                    {/* Dashed circles */}
                                    <svg
                                        className="absolute inset-0 w-full h-full"
                                        viewBox="0 0 320 320"
                                    >
                                        {/* Outer circle */}
                                        <circle
                                            cx="160"
                                            cy="160"
                                            r="155"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1"
                                            strokeDasharray="4 4"
                                            className="text-gray-200 dark:text-grayDark-700"
                                        />
                                        {/* Middle circle */}
                                        <circle
                                            cx="160"
                                            cy="160"
                                            r="115"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1"
                                            strokeDasharray="4 4"
                                            className="text-gray-200 dark:text-grayDark-700"
                                        />
                                        {/* Inner circle */}
                                        <circle
                                            cx="160"
                                            cy="160"
                                            r="75"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1"
                                            strokeDasharray="4 4"
                                            className="text-gray-200 dark:text-grayDark-700"
                                        />
                                    </svg>

                                    {/* Center logo */}
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="shadow-xl">
                                            <Logo icon ratio={1.9} />
                                        </div>
                                    </div>

                                    {/* Floating icons - organic placement */}
                                    {/* Video icon - top left */}
                                    <div className="absolute top-[8%] left-[25%] w-5 h-5 text-gray-400 dark:text-grayDark-500">
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                        >
                                            <path d="M2 11.5c0-3.287 0-4.931.908-6.038a4 4 0 0 1 .554-.554C4.57 4 6.212 4 9.5 4c3.287 0 4.931 0 6.038.908a4 4 0 0 1 .554.554C17 6.57 17 8.212 17 11.5v1c0 3.287 0 4.931-.908 6.038a4 4 0 0 1-.554.554C14.43 20 12.788 20 9.5 20c-3.287 0-4.931 0-6.038-.908a4 4 0 0 1-.554-.554C2 17.43 2 15.788 2 12.5zm15-2l.658-.329c1.946-.973 2.92-1.46 3.63-1.02c.712.44.712 1.528.712 3.703v.292c0 2.176 0 3.263-.711 3.703c-.712.44-1.685-.047-3.63-1.02L17 14.5z" />
                                        </svg>
                                    </div>
                                    {/* Document icon - top right */}
                                    <div className="absolute top-[5%] right-[25%] w-5 h-5 text-gray-400 dark:text-grayDark-500">
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                        >
                                            <path d="M3 10c0-3.771 0-5.657 1.172-6.828S7.229 2 11 2h2c3.771 0 5.657 0 6.828 1.172S21 6.229 21 10v4c0 3.771 0 5.657-1.172 6.828S16.771 22 13 22h-2c-3.771 0-5.657 0-6.828-1.172S3 17.771 3 14z" />
                                            <path
                                                strokeLinecap="round"
                                                d="M8 12h8M8 8h8m-8 8h5"
                                            />
                                        </svg>
                                    </div>
                                    {/* Book icon - top center */}
                                    <div className="absolute top-[12%] left-[48%] w-4 h-4 text-gray-300 dark:text-grayDark-600">
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                        >
                                            <path d="M4 8c0-2.828 0-4.243.879-5.121C5.757 2 7.172 2 10 2h4c2.828 0 4.243 0 5.121.879C20 3.757 20 5.172 20 8v8c0 2.828 0 4.243-.879 5.121C18.243 22 16.828 22 14 22h-4c-2.828 0-4.243 0-5.121-.879C4 20.243 4 18.828 4 16z" />
                                            <path d="M19.898 16h-12c-.93 0-1.395 0-1.777.102A3 3 0 0 0 4 18.224" />
                                            <path
                                                strokeLinecap="round"
                                                d="M8 7h8m-8 3.5h5m6.5 8.5H8"
                                            />
                                        </svg>
                                    </div>

                                    {/* Left area */}
                                    {/* Certificate icon */}
                                    <div className="absolute top-[22%] left-[8%] w-5 h-5 text-gray-400 dark:text-grayDark-500">
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                        >
                                            <circle cx="12" cy="16" r="3" />
                                            <path d="m12 19.26l-2.263 2.17c-.324.31-.486.465-.623.52c-.313.122-.66.017-.824-.25c-.072-.117-.095-.328-.14-.75c-.025-.239-.038-.358-.077-.458a.83.83 0 0 0-.5-.48c-.104-.037-.229-.049-.477-.073c-.44-.044-.661-.065-.783-.134a.617.617 0 0 1-.26-.79c.056-.132.218-.287.542-.598L8.073 17l1.04-1.04M12 19.26l2.263 2.17c.324.31.486.465.623.52c.313.122.66.017.824-.25c.072-.117.095-.328.14-.75c.025-.239.038-.358.077-.458a.83.83 0 0 1 .5-.48c.105-.037.229-.049.477-.073c.44-.043.661-.065.783-.134a.617.617 0 0 0 .26-.79c-.056-.132-.218-.287-.542-.598L15.926 17L15 16.073" />
                                            <path d="M17.32 17.996c1.972-.021 3.072-.145 3.801-.875C22 16.243 22 14.828 22 12V8c0-2.828 0-4.243-.879-5.121C20.243 2 18.828 2 16 2H8c-2.828 0-4.243 0-5.121.879C2 3.757 2 5.172 2 8v4c0 2.828 0 4.243.879 5.121c.768.769 1.946.865 4.121.877" />
                                            <path
                                                strokeLinecap="round"
                                                d="M9 6h6M7 9.5h10"
                                            />
                                        </svg>
                                    </div>
                                    {/* Chart icon */}
                                    <div className="absolute top-[35%] left-[18%] w-4 h-4 text-gray-300 dark:text-grayDark-600">
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                        >
                                            <path d="M2.75 2a.75.75 0 0 0-1.5 0v10.057c0 2.309 0 4.118.19 5.53c.194 1.444.6 2.584 1.494 3.479c.895.895 2.035 1.3 3.48 1.494c1.411.19 3.22.19 5.529.19H22a.75.75 0 0 0 0-1.5H12c-2.378 0-4.086-.002-5.386-.176c-1.279-.172-2.05-.5-2.62-1.069c-.569-.57-.896-1.34-1.068-2.619c-.174-1.3-.176-3.008-.176-5.386z" />
                                            <path d="M19.588 7.466a.75.75 0 1 0-1.175-.932l-3.118 3.926c-.245.309-.406.51-.539.656c-.13.143-.19.183-.218.198a.75.75 0 0 1-.678.014c-.028-.013-.09-.05-.227-.187c-.138-.14-.308-.335-.566-.633l-.017-.018c-.237-.274-.44-.508-.62-.688a2.3 2.3 0 0 0-.64-.483a2.25 2.25 0 0 0-2.035.044a2.3 2.3 0 0 0-.62.51c-.17.188-.363.43-.589.715l-3.133 3.946a.75.75 0 1 0 1.174.932l3.119-3.926c.245-.309.406-.51.538-.656c.13-.143.19-.183.218-.198a.75.75 0 0 1 .678-.014c.029.013.091.05.227.187c.139.14.308.335.567.633l.016.018c.238.274.44.508.62.688c.185.187.389.362.64.483a2.25 2.25 0 0 0 2.035-.044a2.4 2.4 0 0 0 .62-.51c.17-.188.363-.43.59-.715z" />
                                        </svg>
                                    </div>
                                    {/* Education icon */}
                                    <div className="absolute top-[45%] left-[3%] w-5 h-5 text-gray-400 dark:text-grayDark-500">
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                        >
                                            <path d="M9.783 3.5a5.17 5.17 0 0 1 4.434 0l6.691 3.137c1.456.682 1.456 3.044 0 3.726l-6.69 3.137a5.17 5.17 0 0 1-4.435 0l-6.691-3.137c-1.456-.682-1.456-3.044 0-3.726z" />
                                            <path
                                                strokeLinecap="round"
                                                d="M2 8.5V14m17-2.5v5.125c0 1.008-.503 1.952-1.385 2.44C16.146 19.88 13.796 21 12 21s-4.146-1.121-5.615-1.934C5.504 18.577 5 17.633 5 16.626V11.5"
                                            />
                                        </svg>
                                    </div>
                                    {/* Video small */}
                                    <div className="absolute top-[55%] left-[15%] w-4 h-4 text-gray-300 dark:text-grayDark-600">
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                        >
                                            <path d="M2 11.5c0-3.287 0-4.931.908-6.038a4 4 0 0 1 .554-.554C4.57 4 6.212 4 9.5 4c3.287 0 4.931 0 6.038.908a4 4 0 0 1 .554.554C17 6.57 17 8.212 17 11.5v1c0 3.287 0 4.931-.908 6.038a4 4 0 0 1-.554.554C14.43 20 12.788 20 9.5 20c-3.287 0-4.931 0-6.038-.908a4 4 0 0 1-.554-.554C2 17.43 2 15.788 2 12.5zm15-2l.658-.329c1.946-.973 2.92-1.46 3.63-1.02c.712.44.712 1.528.712 3.703v.292c0 2.176 0 3.263-.711 3.703c-.712.44-1.685-.047-3.63-1.02L17 14.5z" />
                                        </svg>
                                    </div>
                                    {/* Document bottom left */}
                                    <div className="absolute top-[65%] left-[10%] w-5 h-5 text-gray-400 dark:text-grayDark-500">
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                        >
                                            <path d="M3 10c0-3.771 0-5.657 1.172-6.828S7.229 2 11 2h2c3.771 0 5.657 0 6.828 1.172S21 6.229 21 10v4c0 3.771 0 5.657-1.172 6.828S16.771 22 13 22h-2c-3.771 0-5.657 0-6.828-1.172S3 17.771 3 14z" />
                                            <path
                                                strokeLinecap="round"
                                                d="M8 12h8M8 8h8m-8 8h5"
                                            />
                                        </svg>
                                    </div>

                                    {/* Right area */}
                                    {/* Book */}
                                    <div className="absolute top-[20%] right-[5%] w-5 h-5 text-gray-400 dark:text-grayDark-500">
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                        >
                                            <path d="M4 8c0-2.828 0-4.243.879-5.121C5.757 2 7.172 2 10 2h4c2.828 0 4.243 0 5.121.879C20 3.757 20 5.172 20 8v8c0 2.828 0 4.243-.879 5.121C18.243 22 16.828 22 14 22h-4c-2.828 0-4.243 0-5.121-.879C4 20.243 4 18.828 4 16z" />
                                            <path d="M19.898 16h-12c-.93 0-1.395 0-1.777.102A3 3 0 0 0 4 18.224" />
                                            <path
                                                strokeLinecap="round"
                                                d="M8 7h8m-8 3.5h5m6.5 8.5H8"
                                            />
                                        </svg>
                                    </div>
                                    {/* Education small */}
                                    <div className="absolute top-[32%] right-[15%] w-4 h-4 text-gray-300 dark:text-grayDark-600">
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                        >
                                            <path d="M9.783 3.5a5.17 5.17 0 0 1 4.434 0l6.691 3.137c1.456.682 1.456 3.044 0 3.726l-6.69 3.137a5.17 5.17 0 0 1-4.435 0l-6.691-3.137c-1.456-.682-1.456-3.044 0-3.726z" />
                                            <path
                                                strokeLinecap="round"
                                                d="M2 8.5V14m17-2.5v5.125c0 1.008-.503 1.952-1.385 2.44C16.146 19.88 13.796 21 12 21s-4.146-1.121-5.615-1.934C5.504 18.577 5 17.633 5 16.626V11.5"
                                            />
                                        </svg>
                                    </div>
                                    {/* Certificate */}
                                    <div className="absolute top-[42%] right-[2%] w-5 h-5 text-gray-400 dark:text-grayDark-500">
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                        >
                                            <circle cx="12" cy="16" r="3" />
                                            <path d="m12 19.26l-2.263 2.17c-.324.31-.486.465-.623.52c-.313.122-.66.017-.824-.25c-.072-.117-.095-.328-.14-.75c-.025-.239-.038-.358-.077-.458a.83.83 0 0 0-.5-.48c-.104-.037-.229-.049-.477-.073c-.44-.044-.661-.065-.783-.134a.617.617 0 0 1-.26-.79c.056-.132.218-.287.542-.598L8.073 17l1.04-1.04M12 19.26l2.263 2.17c.324.31.486.465.623.52c.313.122.66.017.824-.25c.072-.117.095-.328.14-.75c.025-.239.038-.358.077-.458a.83.83 0 0 1 .5-.48c.105-.037.229-.049.477-.073c.44-.043.661-.065.783-.134a.617.617 0 0 0 .26-.79c-.056-.132-.218-.287-.542-.598L15.926 17L15 16.073" />
                                            <path d="M17.32 17.996c1.972-.021 3.072-.145 3.801-.875C22 16.243 22 14.828 22 12V8c0-2.828 0-4.243-.879-5.121C20.243 2 18.828 2 16 2H8c-2.828 0-4.243 0-5.121.879C2 3.757 2 5.172 2 8v4c0 2.828 0 4.243.879 5.121c.768.769 1.946.865 4.121.877" />
                                            <path
                                                strokeLinecap="round"
                                                d="M9 6h6M7 9.5h10"
                                            />
                                        </svg>
                                    </div>
                                    {/* Chart small */}
                                    <div className="absolute top-[52%] right-[12%] w-4 h-4 text-gray-300 dark:text-grayDark-600">
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                        >
                                            <path d="M2.75 2a.75.75 0 0 0-1.5 0v10.057c0 2.309 0 4.118.19 5.53c.194 1.444.6 2.584 1.494 3.479c.895.895 2.035 1.3 3.48 1.494c1.411.19 3.22.19 5.529.19H22a.75.75 0 0 0 0-1.5H12c-2.378 0-4.086-.002-5.386-.176c-1.279-.172-2.05-.5-2.62-1.069c-.569-.57-.896-1.34-1.068-2.619c-.174-1.3-.176-3.008-.176-5.386z" />
                                            <path d="M19.588 7.466a.75.75 0 1 0-1.175-.932l-3.118 3.926c-.245.309-.406.51-.539.656c-.13.143-.19.183-.218.198a.75.75 0 0 1-.678.014c-.028-.013-.09-.05-.227-.187c-.138-.14-.308-.335-.566-.633l-.017-.018c-.237-.274-.44-.508-.62-.688a2.3 2.3 0 0 0-.64-.483a2.25 2.25 0 0 0-2.035.044a2.3 2.3 0 0 0-.62.51c-.17.188-.363.43-.589.715l-3.133 3.946a.75.75 0 1 0 1.174.932l3.119-3.926c.245-.309.406-.51.538-.656c.13-.143.19-.183.218-.198a.75.75 0 0 1 .678-.014c.029.013.091.05.227.187c.139.14.308.335.567.633l.016.018c.238.274.44.508.62.688c.185.187.389.362.64.483a2.25 2.25 0 0 0 2.035-.044a2.4 2.4 0 0 0 .62-.51c.17-.188.363-.43.59-.715z" />
                                        </svg>
                                    </div>
                                    {/* Video */}
                                    <div className="absolute top-[62%] right-[8%] w-5 h-5 text-gray-400 dark:text-grayDark-500">
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                        >
                                            <path d="M2 11.5c0-3.287 0-4.931.908-6.038a4 4 0 0 1 .554-.554C4.57 4 6.212 4 9.5 4c3.287 0 4.931 0 6.038.908a4 4 0 0 1 .554.554C17 6.57 17 8.212 17 11.5v1c0 3.287 0 4.931-.908 6.038a4 4 0 0 1-.554.554C14.43 20 12.788 20 9.5 20c-3.287 0-4.931 0-6.038-.908a4 4 0 0 1-.554-.554C2 17.43 2 15.788 2 12.5zm15-2l.658-.329c1.946-.973 2.92-1.46 3.63-1.02c.712.44.712 1.528.712 3.703v.292c0 2.176 0 3.263-.711 3.703c-.712.44-1.685-.047-3.63-1.02L17 14.5z" />
                                        </svg>
                                    </div>

                                    {/* Bottom area */}
                                    {/* Education */}
                                    <div className="absolute bottom-[18%] left-[25%] w-5 h-5 text-gray-400 dark:text-grayDark-500">
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                        >
                                            <path d="M9.783 3.5a5.17 5.17 0 0 1 4.434 0l6.691 3.137c1.456.682 1.456 3.044 0 3.726l-6.69 3.137a5.17 5.17 0 0 1-4.435 0l-6.691-3.137c-1.456-.682-1.456-3.044 0-3.726z" />
                                            <path
                                                strokeLinecap="round"
                                                d="M2 8.5V14m17-2.5v5.125c0 1.008-.503 1.952-1.385 2.44C16.146 19.88 13.796 21 12 21s-4.146-1.121-5.615-1.934C5.504 18.577 5 17.633 5 16.626V11.5"
                                            />
                                        </svg>
                                    </div>
                                    {/* Document small */}
                                    <div className="absolute bottom-[25%] left-[38%] w-4 h-4 text-gray-300 dark:text-grayDark-600">
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                        >
                                            <path d="M3 10c0-3.771 0-5.657 1.172-6.828S7.229 2 11 2h2c3.771 0 5.657 0 6.828 1.172S21 6.229 21 10v4c0 3.771 0 5.657-1.172 6.828S16.771 22 13 22h-2c-3.771 0-5.657 0-6.828-1.172S3 17.771 3 14z" />
                                            <path
                                                strokeLinecap="round"
                                                d="M8 12h8M8 8h8m-8 8h5"
                                            />
                                        </svg>
                                    </div>
                                    {/* Book */}
                                    <div className="absolute bottom-[12%] right-[30%] w-5 h-5 text-gray-400 dark:text-grayDark-500">
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                        >
                                            <path d="M4 8c0-2.828 0-4.243.879-5.121C5.757 2 7.172 2 10 2h4c2.828 0 4.243 0 5.121.879C20 3.757 20 5.172 20 8v8c0 2.828 0 4.243-.879 5.121C18.243 22 16.828 22 14 22h-4c-2.828 0-4.243 0-5.121-.879C4 20.243 4 18.828 4 16z" />
                                            <path d="M19.898 16h-12c-.93 0-1.395 0-1.777.102A3 3 0 0 0 4 18.224" />
                                            <path
                                                strokeLinecap="round"
                                                d="M8 7h8m-8 3.5h5m6.5 8.5H8"
                                            />
                                        </svg>
                                    </div>
                                    {/* Certificate small */}
                                    <div className="absolute bottom-[22%] right-[18%] w-4 h-4 text-gray-300 dark:text-grayDark-600">
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                        >
                                            <circle cx="12" cy="16" r="3" />
                                            <path d="m12 19.26l-2.263 2.17c-.324.31-.486.465-.623.52c-.313.122-.66.017-.824-.25c-.072-.117-.095-.328-.14-.75c-.025-.239-.038-.358-.077-.458a.83.83 0 0 0-.5-.48c-.104-.037-.229-.049-.477-.073c-.44-.044-.661-.065-.783-.134a.617.617 0 0 1-.26-.79c.056-.132.218-.287.542-.598L8.073 17l1.04-1.04M12 19.26l2.263 2.17c.324.31.486.465.623.52c.313.122.66.017.824-.25c.072-.117.095-.328.14-.75c.025-.239.038-.358.077-.458a.83.83 0 0 1 .5-.48c.105-.037.229-.049.477-.073c.44-.043.661-.065.783-.134a.617.617 0 0 0 .26-.79c-.056-.132-.218-.287-.542-.598L15.926 17L15 16.073" />
                                            <path d="M17.32 17.996c1.972-.021 3.072-.145 3.801-.875C22 16.243 22 14.828 22 12V8c0-2.828 0-4.243-.879-5.121C20.243 2 18.828 2 16 2H8c-2.828 0-4.243 0-5.121.879C2 3.757 2 5.172 2 8v4c0 2.828 0 4.243.879 5.121c.768.769 1.946.865 4.121.877" />
                                            <path
                                                strokeLinecap="round"
                                                d="M9 6h6M7 9.5h10"
                                            />
                                        </svg>
                                    </div>
                                </div>

                                {/* Gradient overlays for fade effect */}
                                <div className="absolute inset-x-0 top-0 h-4 bg-gradient-to-b from-white dark:from-grayDark-900 to-transparent pointer-events-none" />
                                <div className="absolute inset-x-0 bottom-0 h-4 bg-gradient-to-t from-white dark:from-grayDark-900 to-transparent pointer-events-none" />
                            </div>

                            <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3">
                                {t("howTeamsLearnTitle")}
                            </h3>
                            <p className="text-gray-500 dark:text-grayDark-400 text-xs sm:text-sm mb-6 sm:mb-8">
                                {t("howTeamsLearnSubtitle")}
                            </p>

                            <div className="space-y-3 sm:space-y-4">
                                {[
                                    t("howTeamsLearnPoint1"),
                                    t("howTeamsLearnPoint2"),
                                    t("howTeamsLearnPoint3"),
                                    t("howTeamsLearnPoint4"),
                                    t("howTeamsLearnPoint5"),
                                    t("howTeamsLearnPoint6"),
                                ].map((item, i) => (
                                    <div
                                        key={i}
                                        className="flex items-start gap-2 sm:gap-3"
                                    >
                                        <SolarIcon name="like-linear" className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0 mt-0.5" />
                                        <span className="text-gray-700 dark:text-grayDark-300 text-xs sm:text-sm">
                                            {item}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section className="py-16 sm:py-24 px-4">
                <div className="max-w-6xl mx-auto">
                    <div className="grid xl:grid-cols-[1fr_400px] gap-12 xl:gap-16 items-center">
                        {/* Left - Text content */}
                        <div>
                            <p className="text-xs sm:text-sm font-semibold text-primary mb-3 sm:mb-4 tracking-wide">
                                {t("pricingTag")}
                            </p>
                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
                                {t("pricingTitle")}
                                <br />
                                {t("pricingTitleLine2")}
                            </h2>
                            <p className="text-base sm:text-lg text-gray-500 dark:text-grayDark-400 mb-8 leading-relaxed max-w-lg">
                                {t("pricingSubtitle")}
                            </p>

                            {/* Features list */}
                            <div className="space-y-4 mb-8">
                                {[
                                    {
                                        title: t("pricingFeature1Title"),
                                        desc: t("pricingFeature1Desc"),
                                    },
                                    {
                                        title: t("pricingFeature2Title"),
                                        desc: t("pricingFeature2Desc"),
                                    },
                                    {
                                        title: t("pricingFeature3Title"),
                                        desc: t("pricingFeature3Desc"),
                                    },
                                ].map((item, i) => (
                                    <div key={i} className="flex gap-4">
                                        <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-grayDark-800 flex items-center justify-center flex-shrink-0">
                                            <SolarIcon name="check-circle-linear" className="w-5 h-5 text-primary" />
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-900 dark:text-white text-sm sm:text-base mb-1">
                                                {item.title}
                                            </p>
                                            <p className="text-sm text-gray-500 dark:text-grayDark-400 mb-0">
                                                {item.desc}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* <p className="text-gray-500 dark:text-grayDark-400 text-sm">
                                Plus de 50 utilisateurs ?{" "}
                                <a
                                    href="mailto:teams@believemy.com"
                                    className="text-primary hover:underline font-medium"
                                >
                                    Contactez-nous pour un tarif personnalisé
                                </a>
                            </p> */}
                        </div>

                        {/* Right - Pricing card */}
                        <div className="relative">
                            {/* Background decoration */}
                            <div className="absolute -inset-4 bg-gradient-to-br from-primary/10 via-transparent to-transparent rounded-[2rem] blur-2xl -z-10" />

                            <div className="bg-gray-950 dark:bg-grayDark-900 rounded-3xl p-8 sm:p-10 border border-grayDark-800">
                                {/* Price */}
                                <div className="text-center mb-8">
                                    <p className="text-gray-400 text-sm mb-3">
                                        {t("pricingPerUser")}
                                    </p>
                                    <div className="flex items-baseline justify-center gap-2 mb-2">
                                        <span className="text-5xl sm:text-6xl font-bold text-white tracking-tight">
                                            {parseInt(
                                                process.env
                                                    .NEXT_PUBLIC_PRICE_PER_SEAT_MONTHLY_FOR_TEAMS,
                                            ) * 12}
                                            €
                                        </span>
                                        <span className="text-gray-500 text-lg">
                                            {t("pricingPerYear")}
                                        </span>
                                    </div>
                                    <p className="text-gray-500 text-sm">
                                        {t("pricingMonthly", { price: parseInt(process.env.NEXT_PUBLIC_PRICE_PER_SEAT_MONTHLY_FOR_TEAMS) })}
                                    </p>
                                </div>

                                {/* Mini features */}
                                <div className="grid grid-cols-2 gap-3 mb-8">
                                    {[
                                        t("pricingFeatureAll"),
                                        t("pricingFeatureDashboard"),
                                        t("pricingFeatureCertificates"),
                                        t("pricingFeatureSupport"),
                                    ].map((feature, i) => (
                                        <div
                                            key={i}
                                            className="flex items-center gap-2 text-gray-300 text-sm"
                                        >
                                            <svg
                                                className="w-4 h-4 text-primary flex-shrink-0"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                            >
                                                <path
                                                    d="M5 13l4 4L19 7"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                            {feature}
                                        </div>
                                    ))}
                                </div>

                                {/* CTA */}
                                <Link
                                    href={`/${locale}/teams/signup`}
                                    className="group block w-full text-center bg-white btn-3d text-gray-900 px-6 py-4 rounded-2xl font-semibold hover:bg-gray-100 transition-colors"
                                >
                                    <span className="flex items-center justify-center gap-2">
                                        {teamsTrialEnabled
                                            ? t("ctaTrial")
                                            : t("ctaNoTrial")}
                                        <SolarIcon name="arrow-right-linear" className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                                    </span>
                                </Link>
                                {teamsTrialEnabled && (
                                    <p className="text-center text-gray-500 text-xs mt-4 mb-0">
                                        {t("pricingTrialDays", { days: teamsTrialDays })}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ - Uxcel Style 2 columns */}
            <section className="py-16 sm:py-24 px-4">
                <div className="max-w-6xl mx-auto">
                    <div className="grid xl:grid-cols-[300px_1fr] gap-8 xl:gap-16">
                        {/* Left - Title */}
                        <div className="mb-2 xl:mb-0">
                            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
                                {t("faqTitle")}
                            </h2>
                            <p className="text-gray-500 dark:text-grayDark-400 text-sm sm:text-base">
                                {t("faqContact")}{" "}
                                <Link
                                    href={`/${locale}/contact`}
                                    className="text-primary hover:underline"
                                >
                                    {t("faqContactLink")}
                                </Link>
                            </p>
                        </div>

                        {/* Right - Accordions */}
                        <div className="space-y-3">
                            {faqs.map((faq, index) => (
                                <div
                                    key={index}
                                    className="bg-white dark:bg-grayDark-900 rounded-2xl border border-gray-200 dark:border-grayDark-800 overflow-hidden"
                                >
                                    <button
                                        onClick={() =>
                                            setOpenFaq(
                                                openFaq === index
                                                    ? null
                                                    : index,
                                            )
                                        }
                                        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 dark:hover:bg-grayDark-800 transition-colors"
                                    >
                                        <span className="font-medium text-gray-900 dark:text-white pr-4">
                                            {faq.question}
                                        </span>
                                        <motion.div
                                            animate={{
                                                rotate:
                                                    openFaq === index ? 180 : 0,
                                            }}
                                            transition={{
                                                duration: 0.3,
                                                ease: [0.4, 0, 0.2, 1],
                                            }}
                                        >
                                            <SolarIcon name="alt-arrow-down-linear" className="w-5 h-5 text-gray-400 dark:text-grayDark-500" />
                                        </motion.div>
                                    </button>
                                    <AnimatePresence initial={false}>
                                        {openFaq === index && (
                                            <motion.div
                                                initial={{
                                                    height: 0,
                                                    opacity: 0,
                                                }}
                                                animate={{
                                                    height: "auto",
                                                    opacity: 1,
                                                }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{
                                                    height: {
                                                        duration: 0.3,
                                                        ease: [0.4, 0, 0.2, 1],
                                                    },
                                                    opacity: {
                                                        duration: 0.25,
                                                        delay: 0.05,
                                                    },
                                                }}
                                                className="overflow-hidden"
                                            >
                                                <div className="px-5 pt-2 pb-6 text-gray-500 dark:text-grayDark-400 leading-relaxed">
                                                    {faq.answer}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA - Dark Uxcel Style */}
            <section className="py-8 px-4 mb-5">
                <div className="max-w-6xl mx-auto">
                    <div className="bg-gray-950 dark:bg-grayDark-900 rounded-3xl p-6 sm:p-10 xl:p-16 border border-grayDark-800">
                        <div className="grid xl:grid-cols-2 gap-8 sm:gap-12 items-center">
                            {/* Left - Text */}
                            <div>
                                <h2 className="text-2xl sm:text-3xl xl:text-4xl font-bold text-white mb-4 leading-tight">
                                    {t("finalCtaTitle")}
                                </h2>
                                <p className="text-gray-400 leading-relaxed">
                                    {t("finalCtaSubtitle", { count: process.env.NEXT_PUBLIC_NUMBER_OF_TEAMS })}
                                </p>
                            </div>

                            {/* Right - Logos + CTAs */}
                            <div>
                                {/* Company Logos */}
                                <div className="grid grid-cols-3 gap-6 mb-10">
                                    {trustedLogos.map((logo, i) => (
                                        <div
                                            key={i}
                                            className="flex justify-center items-center"
                                        >
                                            <img
                                                src={logo.src}
                                                alt={logo.alt}
                                                className="object-contain h-[20px] opacity-50 grayscale"
                                            />
                                        </div>
                                    ))}
                                </div>

                                {/* CTAs */}
                                <div className="space-y-3">
                                    <Link
                                        href={`/${locale}/teams/signup`}
                                        className="block w-full text-center bg-primary btn-3d lip-orange text-white px-8 py-4 rounded-full font-medium hover:bg-primary/90 hover:text-white transition-all"
                                    >
                                        {teamsTrialEnabled ? t("ctaTrial") : t("ctaNoTrial")}
                                    </Link>
                                    <Link
                                        href={`/${locale}/teams/demo`}
                                        className="block w-full text-center text-white font-medium hover:text-gray-300 transition-all py-2"
                                    >
                                        {t("ctaDemo")}
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
